import type { DeliveryLockSelection } from '@/services/deliveryLockService';
import { calculatePaymentCount } from '@/types/servicing';

export type EncompassFieldWrite = { id: string; value: string };

// The Investor Loan # copies (996, VEND.X276) are not built here: the Funded
// milestone call asks update-encompass-milestone to copy Encompass's own 352
// (`syncInvestorLoanCopies`), so a number saved during finalize is not
// overwritten with one read before it.
export interface BankerReconFinalizationFieldWriteInput {
  fundsToEscrow?: string | null;
  fundsReceived?: string | null;
  investorPurchaseDate?: string | null;
  firstPaymentDate?: string | null;
  firstPaymentToInvestor?: string | null;
  deliveryLockSelection?: DeliveryLockSelection | null;
  currentEncompassLockExpires?: string | null;
}

function isPresent(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function formatEncompassDateField(value: string | null | undefined): string | null {
  if (!isPresent(value)) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

export function getBankerReconDeliveryByDate(input: Pick<
  BankerReconFinalizationFieldWriteInput,
  'deliveryLockSelection' | 'currentEncompassLockExpires'
>): string | null {
  if (input.deliveryLockSelection) {
    const selectedLockExpires = input.deliveryLockSelection.rateLockFields.rate_lock_buy_side_lock_expires;
    const selectedLockExpiresString = typeof selectedLockExpires === 'string' ? selectedLockExpires : null;
    return isPresent(selectedLockExpiresString)
      ? selectedLockExpiresString
      : null;
  }

  return isPresent(input.currentEncompassLockExpires) ? input.currentEncompassLockExpires : null;
}

export function buildBankerReconFinalizationFieldWrites(
  input: BankerReconFinalizationFieldWriteInput
): EncompassFieldWrite[] {
  const fieldWrites: EncompassFieldWrite[] = [];
  const pushDate = (id: string, value: string | null | undefined) => {
    const formatted = formatEncompassDateField(value);
    if (formatted) {
      fieldWrites.push({ id, value: formatted });
    }
  };

  pushDate('3341', input.fundsToEscrow);
  pushDate('3337', input.investorPurchaseDate);
  pushDate('2370', input.fundsReceived);
  pushDate('3567', input.fundsReceived);
  pushDate('1999', input.fundsReceived);
  pushDate('3570', input.firstPaymentToInvestor);
  pushDate('3514', input.firstPaymentToInvestor);

  if (isPresent(input.firstPaymentDate) && isPresent(input.firstPaymentToInvestor)) {
    fieldWrites.push({
      id: '2371',
      value: calculatePaymentCount(input.firstPaymentDate, input.firstPaymentToInvestor) > 0 ? 'Lender' : 'Investor'
    });
  }

  pushDate('2013', getBankerReconDeliveryByDate(input));

  return fieldWrites;
}
